import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Typography,
  Divider,
  Box,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as SuccessIcon } from 'common/assets/images/success-check.svg';
import { ReactComponent as FailIcon } from 'common/assets/images/failed.svg';
import axios from 'axios';
import { useScreen } from 'common/hooks/useScreen';
import { getAuthToken } from 'common/authentication/authToken';
import { useSelector } from 'react-redux';
import { TapGlobalMessages } from 'TAP/global';
import { NUMBER_ARRAY } from 'common/global/constants';

/**
 * Converts a UTC timestamp to a "time ago" string in user's local time.
 * @param {string | number | Date} utcInput - UTC timestamp (ISO string, epoch ms, or Date).
 * @returns {string} Human-friendly relative time string.
 */
export function timeAgo(utcInput) {
  const now = new Date();
  const date = new Date(utcInput); // interprets UTC properly
  const diffMs = now.getTime() - date.getTime();
  if (isNaN(diffMs)) return 'Invalid date';
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  // fallback for older dates
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
// Returns valid date or undefined
const toDateOrUndefined = (d) => {
  if (!d && d !== 0) return undefined;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? undefined : dt;
};

// Normalizing response from fetch Notifications API by adding Id if needed and handling null cases
const normalize = (raw, idx) => {
  const inProg = raw.isInProgress === 1 || raw.isInProgress === true;
  const fileName = (raw.fileName || 'Download').toString();
  const reqAt = toDateOrUndefined(raw.requestedAt ?? undefined);
  // prefer server id; else derive a reasonably stable id
  const id =
    raw.id !== undefined ? String(raw.id) : `${fileName}-${reqAt?.getTime() ?? 'na'}-${idx}`;

  return {
    id,
    isInProgress: inProg,
    fileLink: raw.fileLink ?? undefined,
    status: raw.status,
    isRead: !!raw.isRead,
    fileName,
    downloadButton: raw.downloadButton ?? '',
    requestedAt: reqAt,
  };
};

// Endpoints for GET and PUT APIs
const DEFAULT_GET = `${process.env.REACT_APP_API_BASE_URL}notification/download/event`;
const DEFAULT_PUT = `${process.env.REACT_APP_API_BASE_URL}notification/download/event`;

const IN_PROGRESS_STATUS = 'REQUEST_PENDING';
const REQUEST_COMPLETED_STATUS = 'REQUEST_COMPLETED';
const REQUEST_FAILED_STATUS = 'REQUEST_FAILED';
const LARGER_THAN_MILLION_STATUS = 'EXPORT_MORE_THAN_1M';

// ---- Reusable conditional polling hook
const useConditionalPolling = (callback, intervalMs, shouldRun) => {
  const cbRef = useRef(callback);
  const predRef = useRef(shouldRun);
  const intervalRef = useRef(null);
  //Sets the new callback as current when callback changes
  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);
  //Sets the new shouldRun as prediction ref's current when shouldRun changes
  useEffect(() => {
    predRef.current = shouldRun;
  }, [shouldRun]);

  useEffect(() => {
    // If there is an ongoing interval then clear it
    if (intervalRef.current) clearInterval(intervalRef.current);
    // Set new Interval that after intervalMs if the predRef says true then fire the current callback
    intervalRef.current = setInterval(() => {
      if (predRef.current()) {
        // Fire and forget; caller handles in-flight suppression if needed
        cbRef.current();
      }
    }, intervalMs);
    // useEffect cleaner to clear if any current interval else set it to null
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [intervalMs]);
};

const renderStatus = (status) => {
  if (status === REQUEST_COMPLETED_STATUS) {
    return 'Ready to Download';
  } else if (status.toLowerCase() === IN_PROGRESS_STATUS.toLowerCase()) {
    return 'Download in progress';
  } else if (status === LARGER_THAN_MILLION_STATUS) {
    return 'Download failed, Excel limit Exceeded';
  } else {
    return 'Download Failed';
  }
};

// ---- Component ----
export const DownloadNotifications = ({
  userId,
  pollingInterval = 5000,
  getUrl = DEFAULT_GET,
  putUrl = DEFAULT_PUT,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Refs for robust control
  const mountedRef = useRef(true);
  const inProgressRef = useRef(false); // last known "any in progress?"
  const fetchingRef = useRef(false); // prevent overlapping GETs
  const markSeenInFlightRef = useRef(false);

  // Get how many downloads triggered currently
  let activeDownloads = useSelector((state) => state?.pollingCounterRd?.pollingRelatedCount);

  const screen = useScreen();
  let token = getAuthToken();

  // By default mounted is true and sets to false if component is unmounted
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  //Aggregates how many unread notifications
  const computeUnread = useCallback((list) => {
    // Prefer explicit isRead and accumulate all isRead
    return list.reduce(
      (acc, n) =>
        acc +
        // Add zero for read items or in progress items else add 1
        (n.isRead || n.status.toLowerCase() === IN_PROGRESS_STATUS.toLowerCase()
          ? NUMBER_ARRAY.zero
          : NUMBER_ARRAY.one),
      // Default Count as 0
      NUMBER_ARRAY.zero,
    );
  }, []);

  // Calls the GET API
  const fetchNotifications = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    const controller = new AbortController();
    try {
      if (items.length === 0) setLoading(true);
      const res = await axios.get(getUrl, {
        params: { userId },
        headers: { Authorization: `Bearer ${token.idToken}` },
        signal: controller.signal,
      });
      const rawList = Array.isArray(res.data?.data) ? res.data.data : [];
      const normalized = rawList.map(normalize);
      if (!mountedRef.current) return;
      setItems(normalized);
      // Update "any in progress" snapshot
      const anyInProgress = normalized.some((n) => n.isInProgress);
      inProgressRef.current = anyInProgress;
      // Update unread badge
      setUnreadCount(computeUnread(normalized));
    } finally {
      fetchingRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUrl, userId, computeUnread, items.length]);

  // Initial fetch on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const prevActiveDownloadRef = useRef(activeDownloads);
  // If activeDownloads changes and it is greater than previous then triggers fetchNotifications
  useEffect(() => {
    if (activeDownloads > prevActiveDownloadRef.current) {
      prevActiveDownloadRef.current = activeDownloads;
      inProgressRef.current = true;
      fetchNotifications();
    }
  }, [fetchNotifications, activeDownloads]);

  // Polling: only calls API when last known state had anything in progress
  useConditionalPolling(fetchNotifications, pollingInterval, () => inProgressRef.current);

  // Immediate refresh
  const handleOpen = async (e) => {
    setAnchorEl(e.currentTarget);
    // Refresh list to show latest before marking seen
    await fetchNotifications();
  };

  // mark-seen when closing the menu
  const handleClose = async () => {
    if (!markSeenInFlightRef.current) {
      markSeenInFlightRef.current = true;
      try {
        await axios.put(
          putUrl,
          {},
          {
            params: { userId },
            headers: { Authorization: `Bearer ${token.idToken}` },
          },
        );
        if (!mountedRef.current) return;
        // Optimistically mark all as seen locally
        setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
      } catch {
        // Silent failure; do not revert optimistic update per requirements
      } finally {
        markSeenInFlightRef.current = false;
      }
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const renderIcon = (n) => {
    if (n.status === REQUEST_COMPLETED_STATUS) return <SuccessIcon fontSize={'16px'} />;
    if (n.status === REQUEST_FAILED_STATUS || n.status === LARGER_THAN_MILLION_STATUS)
      return <FailIcon fontSize='small' color='error' />;
  };

  return (
    <>
      <IconButton
        color='inherit'
        aria-label='notifications'
        aria-haspopup='true'
        aria-controls={open ? 'notifications-menu' : undefined}
        onClick={handleOpen}
      >
        <Badge badgeContent={unreadCount} color='error' overlap='circular'>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        id='notifications-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            // maxHeight: 420,
            width: screen.isMobile ? 320 : 420,
            '& .MuiList-root': {
              overflow: 'visible',
            },
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* Header */}
        <Box
          px={2}
          sx={{
            display: 'inline-flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Typography variant='h1' fontWeight={600} fontSize={'26px'}>
            Notifications
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider variant='middle' />
        {/* Loading state */}
        {loading && items.length === 0 && (
          <MenuItem disableRipple>
            <CircularProgress size={18} />
            <Typography sx={{ ml: 1.25 }}>Loading…</Typography>
          </MenuItem>
        )}
        {/* Empty state */}
        {!loading && items.length === 0 && (
          <MenuItem disabled>
            <Typography variant='body2'>No notifications</Typography>
          </MenuItem>
        )}
        {/* Items */}
        <Box px={screen.isMobile ? 0 : 2} sx={{ maxHeight: 320, overflowY: 'auto' }}>
          {items.map((n) => {
            const isUnseen = !n.isRead;
            const disabled = n.status !== REQUEST_COMPLETED_STATUS || !n.fileLink;
            // Only render a menu item for the rows which is not in progress
            return n?.status.toLowerCase() === IN_PROGRESS_STATUS.toLowerCase() ? (
              <></>
            ) : (
              <MenuItem
                key={n.id}
                // Stay open on click; just open link if available
                onClick={() => {
                  if (!disabled && n.fileLink) {
                    window.open(n.fileLink, '_blank', 'noopener,noreferrer');
                  }
                }}
                disabled={false} // keep interactive even if failed (to allow reading details)
                sx={{
                  alignItems: 'flex-start',
                  gap: 1,
                  bgcolor: isUnseen ? '#F5F5F5' : 'transparent',
                  cursor: disabled ? 'default' : 'pointer',
                  borderBottom: '1px solid',
                  borderColor: disabled ? '#79747E' : '#D9D9D9',
                  '& .MuiListItemIcon-root': {
                    minWidth: '16px',
                  },
                }}
              >
                <ListItemIcon sx={{ mt: '2px' }}>{renderIcon(n)}</ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {n.status === REQUEST_COMPLETED_STATUS ? (
                        <>
                          <Typography
                            variant='body1'
                            color='text.secondary'
                            display='block'
                            sx={{
                              lineHeight: '16px',
                              color: '#564C47',
                              textWrap: 'wrap',
                              overFlow: 'wrap',
                            }}
                          >
                            Your <strong>download request</strong> has been generated.
                          </Typography>
                          <Typography
                            variant='body1'
                            color='text.secondary'
                            display='block'
                            sx={{ lineHeight: '16px', color: '#564C47' }}
                          >
                            {TapGlobalMessages.clickToDownload}
                          </Typography>
                          <Typography
                            variant='body2'
                            fontWeight={isUnseen ? 700 : 500}
                            color={n.status === REQUEST_FAILED_STATUS ? 'error.main' : '#1C75BC'}
                            sx={{
                              textDecoration: disabled ? 'line-through' : 'underline',
                              cursor: disabled ? 'default' : 'pointer',
                              overflow: 'wrap',
                              textWrap: 'wrap',
                            }}
                          >
                            {n.fileName}
                          </Typography>
                        </>
                      ) : n.status === REQUEST_FAILED_STATUS ? (
                        <Typography
                          variant='body1'
                          color='text.secondary'
                          sx={{ lineHeight: '16px', textWrap: 'wrap', overflow: 'wrap' }}
                        >
                          Your download request for <strong>{n.downloadButton}</strong> has failed
                        </Typography>
                      ) : n.status === LARGER_THAN_MILLION_STATUS ? (
                        <Typography
                          variant='body1'
                          color='text.secondary'
                          sx={{ lineHeight: '16px', textWrap: 'wrap', overflow: 'wrap' }}
                        >
                          Your download request for <strong>{n.downloadButton}</strong> has failed
                          as it contains more than 1 million records, which exceeds Excel’s limit.
                          Please refine your filters.
                        </Typography>
                      ) : (
                        <></>
                      )}
                    </>
                  }
                  secondary={
                    n.status.toLowerCase() !== IN_PROGRESS_STATUS.toLowerCase() ? (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          cursor: 'default',
                        }}
                      >
                        <Typography variant='caption' color='text.secondary'>
                          {renderStatus(n.status)}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {n.requestedAt ? ` ${timeAgo(n.requestedAt)}` : ''}
                        </Typography>
                      </Box>
                    ) : (
                      <></>
                    )
                  }
                  sx={{
                    letterSpacing: 0,
                  }}
                />
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    </>
  );
};
